import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Params } from '@angular/router';

import { Product } from '~shared/models';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import {
  ProductClickApplicationEvent,
  SearchApplicationEvent,
  SelectItemApplicationEventModel,
} from '~shared/modules/application-event/models';
import { DEFAULT_LIMIT, DEFAULT_OFFSET } from '~app/modules/generic/constants';
import { ApplicationSearchEventSource } from '~shared/modules/application-event/constants';
import { ProductsListUrlService } from '~app/modules/generic/services/products-list-url.service';
import { TRACKED_PRODUCT_INDEX_BASE } from '~app/modules/catalog/constants';
import { PriceTypes } from '~shared/modules/price-type-toggler/models/price-types.enum';
import { BehaviorSubject, Subject } from 'rxjs';
import { PriceTypeService } from '~shared/modules/price-type-toggler/services/price-type.service';
import { SearchInputApplicationEventsHandler } from '~app/modules/generic/classes';
import { UrlUtils } from '~app/modules/generic/utils/url.utils';
import { BlackDealsFeatureFlagService } from '~shared/services/black-deals-feature-flag.service';
import { NavigationHelper } from '~shared/services/navigation-helper.service';
import { ViewItemListApplicationEventModel } from '~shared/modules/application-event/models/view-item-list-application-event.model';
import { UrlUtil } from '~shared/utils/url.util';
import { CurrencyService } from '~shared/services/currency.service';
import { takeUntil } from 'rxjs/operators';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { FeatureToggleEnum, NUMBER_10 } from '~shared/constants';

@Component({
  selector: 'app-search-product-list',
  templateUrl: './product-list.component.html',
  styleUrls: ['./product-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductListComponent implements OnChanges, OnDestroy {
  public page: number = 1;
  public limit: number = DEFAULT_LIMIT;
  currentUrl = this.navigationHelper.getCurrentUrl();
  pageType = UrlUtil.getPageTypeFromCurrentUrl(this.currentUrl);
  isBlackDealsEnabled = this.blackDealsFeatureFlagService.isBlackDealsEnabled();

  @Input() products: Product[] | null = null;
  @Input() isSearch: boolean | null = null;
  @Input() isCustomPLPPage: boolean;
  @Input() queryParams: Params;
  @Input() isDesktop: boolean;
  @Input() isMobileOrTablet: boolean;
  @Input() isMobile: boolean;
  @Input() totalCount: number;
  @Input() isSearchFromHeaderBar: boolean;
  @Input() searchCorrection: string;
  @Input() pageNumberWhenUserSearchedFromHeaderBar: number;
  @Input() productsAreLoading: boolean;
  @Input() isTablet: boolean;

  @Output() productVisibilityChange = new EventEmitter<number>();
  @Output() switchOffManualSearch = new EventEmitter<void>();

  destroySubject$ = new Subject();
  priceType$ = new BehaviorSubject<PriceTypes>(PriceTypes.b2b);

  private applicationEventHandler: SearchInputApplicationEventsHandler;

  constructor(
    private priceTypeService: PriceTypeService,
    private applicationEventService: ApplicationEventService,
    private productsListUrlService: ProductsListUrlService,
    private blackDealsFeatureFlagService: BlackDealsFeatureFlagService,
    private navigationHelper: NavigationHelper,
    private currencyService: CurrencyService,
    private featureFlagService: ActiveFeaturesConfigcatService
  ) {
    this.priceTypeService.priceType$
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((priceType: PriceTypes) => this.priceType$.next(priceType));

    this.applicationEventHandler = new SearchInputApplicationEventsHandler(applicationEventService);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.featureFlagService
      .isFeatureEnabledForCurrentCountry$(FeatureToggleEnum.CATEGORY_URL_IDENTIFIER)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((featureFlag) => {
        this.pageType = UrlUtil.getPageTypeFromCurrentUrl(this.currentUrl, featureFlag);
        this.trackGAIfNeeded(changes);
      });
  }

  ngOnDestroy(): void {
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }

  onProductClick(product: Product, priceType: PriceTypes, productIndex: number): void {
    const pageOffset = (this.page - 1) * this.limit + productIndex;
    this.publishProductClickEvent(product, priceType, pageOffset);
    this.publishSelectItemEvent(product, priceType, productIndex);
  }

  onIndexPower10ProductVisible(index: number): void {
    const slicedProducts: Product[] = this.products.slice(index, index + NUMBER_10);
    const startingIndex: number = (+this.queryParams.offset || DEFAULT_OFFSET) + index;
    const selectedCategoryId: string = this.productsListUrlService.getCategory();
    const searchTerm: string = this.productsListUrlService.getSearchTerm();

    this.applicationEventService.publish(
      new ViewItemListApplicationEventModel(
        this.pageType,
        slicedProducts,
        this.priceTypeService.getPriceTypeSync(),
        this.currencyService.getCurrency(),
        startingIndex,
        searchTerm,
        true
      )
    );
  }

  onProductVisibility(isVisible: boolean, productIndex: number): void {
    if (!this.isMobileOrTablet) {
      return;
    }

    if (this.isNotDesiredProductCount(productIndex)) {
      return;
    }

    // We only want to track when the product is visible
    if (!isVisible) {
      return;
    }
  }

  trackGAIfNeeded(changes: SimpleChanges): void {
    if (changes['queryParams']) {
      this.updatePage();
    }

    if (this.products === null || this.isSearch === null) {
      //We don't have to fire anything unless all inputs are valid
      return;
    }

    //Fire event whenever the list of products changed
    if (changes['products']) {
      if (this.isSearchFromHeaderBar) {
        this.fireSearchEventFromHeader();
      }
    }
  }

  fireSearchEventFromHeader(): void {
    const searchTerm = this.productsListUrlService.getSearchTerm();
    this.switchOffManualSearch.emit();
    const eventSource = ApplicationSearchEventSource.Header;
    const pageNumber = this.pageNumberWhenUserSearchedFromHeaderBar;

    this.applicationEventService.publish(
      new SearchApplicationEvent(
        searchTerm,
        eventSource,
        this.searchCorrection,
        false,
        false,
        pageNumber,
        this.totalCount
      )
    );
  }

  private updatePage(): void {
    const offset = +this.queryParams.offset || DEFAULT_OFFSET;
    const limits = +this.queryParams.limit || this.limit;

    this.page = UrlUtils.getCurrentPage(offset, limits);
  }

  private publishProductClickEvent(product: Product, priceType: PriceTypes, productIndex: number): void {
    const pageOffset = (this.page - 1) * this.limit + productIndex;
    const searchTerm = this.productsListUrlService.getSearchTerm();

    // We pass 2 search term for label and searchterm dimensions. So it's not an error.

    this.applicationEventService.publish(
      new ProductClickApplicationEvent(
        this.currentUrl,
        product,
        priceType,
        this.pageType,
        this.isBlackDealsEnabled,
        pageOffset,
        searchTerm,
        searchTerm
      )
    );
  }

  private publishSelectItemEvent(product: Product, priceType: PriceTypes, productIndex: number): void {
    const searchTerm: string = this.productsListUrlService.getSearchTerm();
    this.applicationEventService.publish(
      new SelectItemApplicationEventModel(this.pageType, product, priceType, productIndex, false, '', '', searchTerm)
    );
  }

  private isNotDesiredProductCount(productIndex: number): boolean {
    return productIndex !== 0 && (productIndex + 1) % TRACKED_PRODUCT_INDEX_BASE !== 0;
  }
}
